import React, { useEffect, useState } from "react";
import { Card, Nav, Button, Badge } from "react-bootstrap";
import { fetchNotifications, markNotificationsAsRead, markTicketAsRead, markTicketsAsUnread } from "../../api/overviewApi";
import { motion, AnimatePresence } from "framer-motion";
import * as Icons from "react-icons/fa";
import {
  FaClock, FaTicketAlt, FaBell, FaEye,
  FaCheckDouble, FaTimes, FaInbox, FaExternalLinkAlt, FaCheck, FaExclamationTriangle, FaUndo,
} from "react-icons/fa";
import { useDataCache } from "../../contexts/DataCacheContext";
import TicketDetailModal from "../tables/TicketDetailModal";
import UnrespondedAlert from "./UnrespondedAlert";

// ── Status / Priority helpers ────────────────────────────────────────────────
const STATUS_CONFIG = {
  "waiting customer": {
    label: "Waiting Customer",
    color: "#f97316",
    bg: "rgba(249,115,22,0.1)",
    border: "rgba(249,115,22,0.3)",
  },
  "emergency": {
    label: "Emergency",
    color: "#ef4444",
    bg: "rgba(239,68,68,0.1)",
    border: "rgba(239,68,68,0.3)",
  },
};

const getStatusConfig = (status, priority) => {
  const s = (status || "").toLowerCase();
  const p = String(priority || "").toLowerCase();
  if (s.includes("waiting")) return STATUS_CONFIG["waiting customer"];
  if (p === "4" || p === "emergency") return STATUS_CONFIG["emergency"];
  return { label: status || "Open", color: "#6366f1", bg: "rgba(99,102,241,0.1)", border: "rgba(99,102,241,0.25)" };
};

const getAccentColor = (status, priority) => {
  const s = (status || "").toLowerCase();
  const p = String(priority || "");
  if (p === "4" || p.toLowerCase() === "emergency") return "#ef4444";
  if (s.includes("waiting")) return "#f97316";
  return "#6366f1";
};

// ── Notification Card ─────────────────────────────────────────────────────────
const NotificationCard = ({ ticket, onViewClick, onMarkUnreadClick, isDark, index, isRead }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isPressed, setIsPressed] = useState(false);

  const accentColor  = isRead ? "#22c55e" : getAccentColor(ticket.status, ticket.priority);
  const statusConfig = getStatusConfig(ticket.status, ticket.priority);

  // Strip the status label prefix from the title when it duplicates the badge text
  const rawTitle = ticket.title || "Untitled Ticket";
  const labelPrefix = !isRead && statusConfig.label ? statusConfig.label : "";
  const displayTitle = labelPrefix && (
    rawTitle.startsWith(labelPrefix + ":") || rawTitle.startsWith(labelPrefix + " ")
  )
    ? rawTitle.slice(labelPrefix.length).replace(/^[:\s]+/, "").trim()
    : rawTitle;

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: isRead ? 0.55 : 1, y: 0 }}
      transition={{ duration: 0.22, delay: index * 0.04, ease: "easeOut" }}
      onClick={() => onViewClick(ticket)}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => { setIsHovered(false); setIsPressed(false); }}
      onMouseDown={() => setIsPressed(true)}
      onMouseUp={() => setIsPressed(false)}
      style={{
        marginBottom: 10,
        borderRadius: 12,
        background: isRead
          ? (isDark ? "rgba(34,197,94,0.04)" : "#f0fdf4")
          : (isHovered
            ? (isDark ? "rgba(255,255,255,0.06)" : "#fafafe")
            : (isDark ? "rgba(255,255,255,0.03)" : "#ffffff")),
        border: `1px solid ${isRead
          ? (isDark ? "rgba(34,197,94,0.2)" : "#bbf7d0")
          : (isHovered ? accentColor + "50" : (isDark ? "rgba(255,255,255,0.07)" : "#eeeef5"))}`,
        borderLeft: `3.5px solid ${accentColor}`,
        boxShadow: isRead ? "none" : (isHovered
          ? `0 4px 20px ${accentColor}20, 0 2px 8px rgba(0,0,0,0.06)`
          : "0 1px 4px rgba(0,0,0,0.04)"),
        cursor: "pointer",
        overflow: "hidden",
        transform: isPressed ? "scale(0.992)" : "scale(1)",
        transition: "all 0.15s ease",
        userSelect: "none",
      }}
    >
      <div style={{ padding: "13px 14px", display: "flex", alignItems: "center", gap: 12 }}>

        {/* Icon */}
        <div style={{
          width: 34, height: 34, borderRadius: 9, flexShrink: 0,
          background: isRead ? "rgba(34,197,94,0.12)" : (isHovered ? `${accentColor}22` : `${accentColor}14`),
          display: "flex", alignItems: "center", justifyContent: "center",
          transition: "background 0.15s ease",
        }}>
          {isRead ? <FaCheck size={13} color="#22c55e" /> : <FaBell size={13} color={accentColor} />}
        </div>

        {/* Content */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 4, flexWrap: "wrap" }}>
            {isRead ? (
              <span style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: "0.04em", color: "#22c55e", background: isDark ? "rgba(34,197,94,0.12)" : "#dcfce7", border: "1px solid rgba(34,197,94,0.3)", borderRadius: 5, padding: "2px 7px", whiteSpace: "nowrap" }}>Read</span>
            ) : (
              <span style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: "0.04em", color: statusConfig.color, background: statusConfig.bg, border: `1px solid ${statusConfig.border}`, borderRadius: 5, padding: "2px 7px", whiteSpace: "nowrap" }}>
                {statusConfig.label}
              </span>
            )}
            <span style={{ fontSize: 10.5, color: isDark ? "rgba(255,255,255,0.3)" : "#b8b8cc", fontFamily: "monospace" }}>
              {ticket.ticket_number || ""}
            </span>
          </div>
          <div style={{ fontSize: 13, fontWeight: 600, lineHeight: 1.35, color: isRead ? (isDark ? "rgba(255,255,255,0.4)" : "#8888a0") : (isDark ? "#f0f0f8" : "#18182e"), marginBottom: 4, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", textDecoration: isRead ? "line-through" : "none" }}>
            {displayTitle}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11, color: isDark ? "rgba(255,255,255,0.35)" : "#a0a0b8" }}>
            <FaClock size={9.5} />
            {ticket.last_activity_date || "N/A"}
          </div>
        </div>

        {/* View / Read */}
        {isRead ? (
          <div style={{ flexShrink: 0, display: "flex", alignItems: "center", gap: 6 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 5, padding: "7px 12px", borderRadius: 20, background: isDark ? "rgba(34,197,94,0.1)" : "#dcfce7", color: "#22c55e", fontSize: 11.5, fontWeight: 600 }}>
              <FaCheck size={10} /> Read
            </div>
            <motion.button
              onClick={e => { e.stopPropagation(); onMarkUnreadClick(ticket); }}
              whileHover={{ scale: 1.06 }} whileTap={{ scale: 0.94 }}
              title="Mark as unread"
              style={{
                display: "flex", alignItems: "center", gap: 5,
                padding: "7px 10px", borderRadius: 20,
                background: isDark ? "rgba(255,255,255,0.07)" : "#f0f0f8",
                border: "none",
                color: isDark ? "rgba(255,255,255,0.5)" : "#7070a0",
                fontSize: 11.5, fontWeight: 600, cursor: "pointer",
                whiteSpace: "nowrap",
              }}
            >
              <FaUndo size={10} />
            </motion.button>
          </div>
        ) : (
          <motion.button
            onClick={e => { e.stopPropagation(); onViewClick(ticket); }}
            whileHover={{ scale: 1.06 }} whileTap={{ scale: 0.94 }}
            style={{
              flexShrink: 0, display: "flex", alignItems: "center", gap: 6,
              padding: "7px 14px", borderRadius: 20,
              background: isHovered ? accentColor : (isDark ? "rgba(255,255,255,0.07)" : "#f0f0f8"),
              border: "none",
              color: isHovered ? "#ffffff" : (isDark ? "rgba(255,255,255,0.5)" : "#7070a0"),
              fontSize: 12, fontWeight: 600, cursor: "pointer",
              transition: "background 0.15s ease, color 0.15s ease",
              whiteSpace: "nowrap",
              boxShadow: isHovered ? `0 4px 12px ${accentColor}40` : "none",
            }}
          >
            <FaEye size={11} /> View
          </motion.button>
        )}
      </div>

      {isHovered && !isRead && (
        <div style={{ height: 2, background: `linear-gradient(90deg, transparent, ${accentColor}60, transparent)` }} />
      )}
    </motion.div>
  );
};

// ── Empty state ───────────────────────────────────────────────────────────────
const EmptyState = ({ isDark }) => (
  <motion.div
    initial={{ opacity: 0, scale: 0.96 }}
    animate={{ opacity: 1, scale: 1 }}
    transition={{ duration: 0.3 }}
    style={{ padding: "48px 24px", textAlign: "center" }}
  >
    <div style={{ width: 52, height: 52, borderRadius: 14, margin: "0 auto 14px", background: isDark ? "rgba(255,255,255,0.05)" : "#f2f2fa", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <FaInbox size={20} color={isDark ? "rgba(255,255,255,0.2)" : "#c0c0d8"} />
    </div>
    <p style={{ fontSize: 13.5, fontWeight: 600, color: isDark ? "rgba(255,255,255,0.35)" : "#9090b0", margin: 0 }}>No notifications here</p>
    <p style={{ fontSize: 11.5, color: isDark ? "rgba(255,255,255,0.18)" : "#c0c0d0", margin: "4px 0 0" }}>You're all caught up!</p>
  </motion.div>
);

// ── Notification Center ───────────────────────────────────────────────────────
const NotificationCenter = ({ selectedClient, currentUser, onClose }) => {
  const { getCachedData, setCachedData } = useDataCache();
  const clientId = selectedClient?.client_id || null;

  const [notifications, setNotifications] = useState(null);
  // Read ?tab= from the URL so direct-navigation / refresh preserves the
  // active tab without requiring a manual click.
  const [activeTab, setActiveTab] = useState(() => {
    const param = new URLSearchParams(window.location.search).get("tab");
    return ["general", "tickets", "system"].includes(param) ? param : "general";
  });
  const [loading, setLoading]             = useState(true);
  const [markingAsRead, setMarkingAsRead] = useState(false);
  const [isDark, setIsDark]               = useState(document.body.classList.contains("dark-mode"));
  const [readIds, setReadIds]             = useState(new Set());
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [showModal, setShowModal]           = useState(false);
  // Tracks count reported back from UnrespondedAlert to gate the empty state
  const [unrespondedCount, setUnrespondedCount] = useState(0);
  // Becomes true only after UnrespondedAlert fires its first onCountChange —
  // prevents the EmptyState from flashing before the Tickets data arrives.
  const [unrespondedLoaded, setUnrespondedLoaded] = useState(false);

  // Reset the Tickets-tab loaded flag whenever the client changes so the
  // skeleton shows again instead of flashing the empty state.
  useEffect(() => { setUnrespondedLoaded(false); }, [clientId]);

  const markAsRead = id => setReadIds(prev => new Set([...prev, id]));
  const markAsUnread = id => setReadIds(prev => {
    const next = new Set(prev);
    next.delete(id);
    return next;
  });

  const handleViewClick = ticket => {
    markAsRead(ticket.id);
    if (ticket.id && clientId) {
      markTicketAsRead(ticket.id, clientId).then(() => {
        window.dispatchEvent(new CustomEvent("notificationTicketRead"));
      });
    }
    setSelectedTicket({
      ...ticket,
      ticket_number:      ticket.ticket_number || null,
      created:            null,
      last_activity_date: ticket.last_activity_date || null,
      queue:              ticket.queue || null,
      description:        ticket.description || null,
    });
    setShowModal(true);
  };

  const handleCloseModal = () => { setShowModal(false); setSelectedTicket(null); };

  const handleMarkUnreadClick = ticket => {
    markAsUnread(ticket.id);
    markTicketsAsUnread([ticket.id], clientId).then(() => {
      window.dispatchEvent(new CustomEvent("notificationTicketUnread"));
    });
  };

  // Dark mode observer
  useEffect(() => {
    const check = () => setIsDark(document.body.classList.contains("dark-mode"));
    const obs = new MutationObserver(check);
    obs.observe(document.body, { attributes: true, attributeFilter: ["class"] });
    window.addEventListener("themechange", check);
    return () => { obs.disconnect(); window.removeEventListener("themechange", check); };
  }, []);

  // Load notifications
  useEffect(() => {
    const load = async () => {
      if (!currentUser?.is_authenticated || !clientId) {
        setNotifications({ general: [], tickets: [], system: [] });
        setLoading(false);
        return;
      }
      const cached = getCachedData("notifications", clientId);
      if (cached !== undefined) {
        setNotifications(cached);
        setLoading(false);
        try {
          const res = await fetchNotifications(clientId);
          if (res?.tickets) {
            const d = { general: res.tickets, tickets: [], system: [] };
            if (res.read_ticket_ids?.length) setReadIds(new Set(res.read_ticket_ids));
            setNotifications(d);
            setCachedData("notifications", d, clientId);
          }
        } catch (err) { console.error("Error refreshing notifications:", err); }
        return;
      }
      setLoading(true);
      try {
        const res = await fetchNotifications(clientId);
        const d = res?.tickets
          ? { general: res.tickets, tickets: [], system: [] }
          : { general: [], tickets: [], system: [] };
        if (res?.read_ticket_ids?.length) setReadIds(new Set(res.read_ticket_ids));
        setNotifications(d);
        setCachedData("notifications", d, clientId);
      } catch (err) {
        console.error("Error fetching notifications:", err);
        setNotifications({ general: [], tickets: [], system: [] });
      } finally { setLoading(false); }
    };
    load();
  }, [selectedClient, activeTab, currentUser?.is_authenticated]);

  const currentNotifications = notifications?.[activeTab] || [];

  const handleMarkAsRead = async () => {
    if (!currentUser?.is_authenticated || !clientId) return;
    const ticketIds = currentNotifications.map(t => t.id).filter(Boolean);
    if (!ticketIds.length) return;
    setReadIds(prev => new Set([...prev, ...ticketIds]));
    setMarkingAsRead(true);
    try {
      await markNotificationsAsRead(clientId, ticketIds);
      window.dispatchEvent(new CustomEvent("notificationsRead"));
    } catch (err) { console.error("Error marking as read:", err); }
    finally { setMarkingAsRead(false); }
  };

  const readCurrentIds = currentNotifications.filter(t => readIds.has(t.id)).map(t => t.id);

  const handleMarkAllUnreadClick = async () => {
    if (!readCurrentIds.length || !clientId) return;
    setReadIds(prev => {
      const next = new Set(prev);
      readCurrentIds.forEach(id => next.delete(id));
      return next;
    });
    try {
      await markTicketsAsUnread(readCurrentIds, clientId);
      window.dispatchEvent(new CustomEvent("notificationTicketUnread"));
    } catch (err) { console.error("Error marking all as unread:", err); }
  };

  if (!currentUser?.is_authenticated) {
    return (
      <div style={{ borderRadius: 16, background: isDark ? "rgba(18,18,30,0.95)" : "#ffffff", border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "#ebebf5"}` }}>
        <EmptyState isDark={isDark} />
      </div>
    );
  }

  const tabs = [
    { key: "general", label: "General", icon: "FaBell"      },
    { key: "tickets", label: "Tickets", icon: "FaTicketAlt" },
    { key: "system",  label: "System",  icon: "FaCog"       },
  ];

  return (
    <>
      <div style={{
        borderRadius: 16,
        background: isDark ? "rgba(18,18,30,0.97)" : "#ffffff",
        border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "#eaeaf5"}`,
        boxShadow: isDark ? "0 8px 40px rgba(0,0,0,0.4)" : "0 8px 32px rgba(0,0,0,0.07)",
        overflow: "hidden",
      }}>

        {/* ── Header ── */}
        <div style={{
          padding: "18px 18px 0",
          background: isDark ? "rgba(255,255,255,0.02)" : "#fafafe",
          borderBottom: `1px solid ${isDark ? "rgba(255,255,255,0.06)" : "#eeeef8"}`,
        }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{
                width: 34, height: 34, borderRadius: 10,
                background: "linear-gradient(135deg, #6366f1, #8b5cf6)",
                display: "flex", alignItems: "center", justifyContent: "center",
                boxShadow: "0 4px 12px rgba(99,102,241,0.3)",
              }}>
                <FaBell size={14} color="#fff" />
              </div>
              <div>
                <h6 style={{ margin: 0, fontSize: 14.5, fontWeight: 700, color: isDark ? "#f0f0f8" : "#18182e" }}>
                  Notifications
                </h6>
                {currentNotifications.length > 0 && (
                  <p style={{ margin: 0, fontSize: 10.5, color: isDark ? "rgba(255,255,255,0.35)" : "#9090b8" }}>
                    {currentNotifications.length} item{currentNotifications.length !== 1 ? "s" : ""} need attention
                    <span style={{ marginLeft: 4, color: "#6366f1", fontWeight: 600 }}>· Click any card to view</span>
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div style={{ display: "flex", gap: 2 }}>
            {tabs.map(tab => {
              const TabIcon = Icons[tab.icon];
              const isActive = activeTab === tab.key;
              // For Tickets tab, show unresponded count with alert styling
              const displayCount = tab.key === "tickets" ? unrespondedCount : (notifications?.[tab.key]?.length || 0);
              const isTicketsTab = tab.key === "tickets";
              
              return (
                <button
                  key={tab.key}
                  onClick={() => {
                    setActiveTab(tab.key);
                    // Keep URL in sync so refresh / direct links land on the right tab
                    const u = new URL(window.location.href);
                    u.searchParams.set("tab", tab.key);
                    window.history.replaceState({}, "", u.toString());
                  }}
                  style={{
                    display: "flex", alignItems: "center", gap: 6,
                    padding: "8px 14px", border: "none", background: "transparent",
                    borderBottom: isActive ? "2px solid #6366f1" : "2px solid transparent",
                    color: isActive ? (isTicketsTab ? "#ef4444" : "#6366f1") : (isDark ? "rgba(255,255,255,0.38)" : "#9090b0"),
                    fontSize: 12, fontWeight: isActive ? 700 : 500,
                    cursor: "pointer", transition: "all 0.15s ease", marginBottom: -1,
                  }}
                >
                  {TabIcon && <TabIcon size={12} />}
                  {/* Add warning icon for Tickets tab when unresponded count > 0 */}
                  {isTicketsTab && unrespondedCount > 0 && (
                    <FaExclamationTriangle size={11} color="#ef4444" style={{ marginRight: -2 }} />
                  )}
                  {tab.label}
                  {displayCount > 0 && (
                    <span style={{
                      fontSize: 10, fontWeight: 700,
                      background: isTicketsTab ? "#ef4444" : (isActive ? "#6366f1" : (isDark ? "rgba(255,255,255,0.1)" : "#e8e8f4")),
                      color: isTicketsTab ? "#fff" : (isActive ? "#fff" : (isDark ? "rgba(255,255,255,0.45)" : "#8080a8")),
                      borderRadius: 20, padding: "1px 6px",
                    }}>
                      {displayCount}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* ── Body ── */}
        <div style={{ padding: "14px 14px 0" }}>
          {loading ? (
            <div style={{ padding: "48px 0", display: "flex", flexDirection: "column", alignItems: "center", gap: 12 }}>
              <div style={{ width: 28, height: 28, borderRadius: "50%", border: "3px solid rgba(99,102,241,0.15)", borderTopColor: "#6366f1", animation: "spin 0.7s linear infinite" }} />
              <span style={{ fontSize: 11.5, color: isDark ? "rgba(255,255,255,0.3)" : "#b0b0c8" }}>Loading notifications...</span>
            </div>
          ) : (
            <div
              style={{ maxHeight: "55vh", overflowY: "auto", overflowX: "hidden", paddingRight: 2, paddingBottom: 4 }}
              className="custom-scrollbar"
            >
              {/* Unresponded alert — top of Tickets tab only */}
              {activeTab === "tickets" && (
                <UnrespondedAlert
                  clientId={clientId}
                  isDark={isDark}
                  onCountChange={count => {
                    setUnrespondedCount(count);
                    setUnrespondedLoaded(true);   // first callback = data arrived
                  }}
                />
              )}

              {currentNotifications.length > 0 ? (
                currentNotifications.map((ticket, i) => (
                  <NotificationCard
                    key={ticket.id || ticket.ticket_number}
                    ticket={ticket}
                    onViewClick={handleViewClick}
                    onMarkUnreadClick={handleMarkUnreadClick}
                    isDark={isDark}
                    index={i}
                    isRead={readIds.has(ticket.id)}
                  />
                ))
              ) : activeTab === "tickets" ? (
                /* Gate on unrespondedLoaded — show skeleton until first count
                   arrives, then only show EmptyState when count is confirmed 0. */
                !unrespondedLoaded ? (
                  <div style={{ padding: "20px 0", display: "flex", flexDirection: "column", alignItems: "center", gap: 10 }}>
                    <div style={{ width: 22, height: 22, borderRadius: "50%", border: "3px solid rgba(239,68,68,0.15)", borderTopColor: "#ef4444", animation: "spin 0.7s linear infinite" }} />
                    <span style={{ fontSize: 11, color: isDark ? "rgba(255,255,255,0.3)" : "#b0b0c8" }}>Loading tickets…</span>
                  </div>
                ) : unrespondedCount === 0 ? (
                  <EmptyState isDark={isDark} />
                ) : null
              ) : (
                <EmptyState isDark={isDark} />
              )}
            </div>
          )}
        </div>

        {/* ── Footer ── */}
        {!loading && currentNotifications.length > 0 && (
          <div style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            padding: "11px 14px",
            borderTop: `1px solid ${isDark ? "rgba(255,255,255,0.06)" : "#eeeef8"}`,
            marginTop: 6,
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 2 }}>
              <button
                onClick={handleMarkAsRead}
                disabled={markingAsRead}
                style={{
                  display: "flex", alignItems: "center", gap: 6,
                  background: "transparent", border: "none",
                  color: isDark ? "rgba(255,255,255,0.38)" : "#9090b0",
                  fontSize: 11.5, fontWeight: 500, cursor: "pointer",
                  padding: "6px 10px", borderRadius: 8, transition: "all 0.15s",
                  opacity: markingAsRead ? 0.5 : 1,
                }}
                onMouseEnter={e => { e.currentTarget.style.background = isDark ? "rgba(255,255,255,0.05)" : "#f2f2fa"; e.currentTarget.style.color = "#6366f1"; }}
                onMouseLeave={e => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = isDark ? "rgba(255,255,255,0.38)" : "#9090b0"; }}
              >
                <FaCheckDouble size={10.5} />
                {markingAsRead ? "Marking..." : "Mark all as read"}
              </button>

              {readCurrentIds.length > 0 && (
                <button
                  onClick={handleMarkAllUnreadClick}
                  style={{
                    display: "flex", alignItems: "center", gap: 6,
                    background: "transparent", border: "none",
                    color: isDark ? "rgba(255,255,255,0.38)" : "#9090b0",
                    fontSize: 11.5, fontWeight: 500, cursor: "pointer",
                    padding: "6px 10px", borderRadius: 8, transition: "all 0.15s",
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = isDark ? "rgba(255,255,255,0.05)" : "#f2f2fa"; e.currentTarget.style.color = "#6366f1"; }}
                  onMouseLeave={e => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = isDark ? "rgba(255,255,255,0.38)" : "#9090b0"; }}
                >
                  <FaUndo size={10} />
                  Mark all as unread
                </button>
              )}
            </div>

            <button
              onClick={onClose}
              style={{
                display: "flex", alignItems: "center", gap: 6,
                background: isDark ? "rgba(239,68,68,0.1)" : "#fff0f0",
                border: `1px solid ${isDark ? "rgba(239,68,68,0.2)" : "#fdd"}`,
                color: "#ef4444", fontSize: 11.5, fontWeight: 600, cursor: "pointer",
                padding: "6px 14px", borderRadius: 8, transition: "all 0.15s",
              }}
              onMouseEnter={e => { e.currentTarget.style.background = "rgba(239,68,68,0.15)"; e.currentTarget.style.borderColor = "rgba(239,68,68,0.35)"; }}
              onMouseLeave={e => { e.currentTarget.style.background = isDark ? "rgba(239,68,68,0.1)" : "#fff0f0"; e.currentTarget.style.borderColor = isDark ? "rgba(239,68,68,0.2)" : "#fdd"; }}
            >
              <FaTimes size={10} /> Close
            </button>
          </div>
        )}
      </div>

      <AnimatePresence>
        {showModal && selectedTicket && (
          <TicketDetailModal ticket={selectedTicket} onClose={handleCloseModal} />
        )}
      </AnimatePresence>

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </>
  );
};

export default NotificationCenter;